import { JobStatus } from '@seek/hirer-job-information';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Box,
  Inline,
  Strong,
  TextLinkButton,
  Badge,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import type { FC } from 'react';

import { TextSkeleton } from 'src/libs/TextSkeleton';

import translations from './.vocab/';
interface Props {
  status: JobStatus;
  numberOfDaysRemaining: number | null;
  billableApplicants?:
    | {
        success: true;
        billableApplicantsCount: number;
        maxBillableApplicants: number;
      }
    | {
        success: false;
      };
  internalReference?: string;
  bucketStatsTotalCount: number;
  openRelevantApplicationDialog: () => void;
}

const JobStatDetail: FC<Props> = ({
  status,
  numberOfDaysRemaining,
  billableApplicants,
  bucketStatsTotalCount,
  openRelevantApplicationDialog,
}) => {
  const { t } = useTranslations(translations);

  const getJobExpiryItem = () => {
    if (numberOfDaysRemaining === null) {
      return undefined;
    }
    if (JobStatus.ACTIVE === status) {
      if (numberOfDaysRemaining === 0) {
        return <Text tone="secondary">{t('Expires today')}</Text>;
      }
      return (
        <Text size="small" tone="secondary">
          {t('statNumber Days left', {
            statNumber: numberOfDaysRemaining,
            Strong: (children) => <Strong>{children}</Strong>,
          })}
        </Text>
      );
    }
    return undefined;
  };

  return (
    <Layout>
      {getJobExpiryItem()}
      <Text size="small" tone="secondary">
        {t('statNumber Applications', {
          statNumber: bucketStatsTotalCount, // FIXME: We shouldn't have undefined bucketStatsTotalCount
          Strong: (children) => <Strong>{children}</Strong>,
        })}
      </Text>
      {billableApplicants && (
        <Inline alignY="center" space="xsmall">
          {billableApplicants.success ? (
            <Box
              cursor="pointer"
              onClick={() => {
                openRelevantApplicationDialog();
              }}
            >
              <Inline space="xxsmall" alignY="center">
                <Box pointerEvents="none">
                  <Badge tone="promote">
                    {billableApplicants.billableApplicantsCount}/
                    {billableApplicants.maxBillableApplicants}
                  </Badge>
                </Box>
                <TextLinkButton>
                  <Text size="small" tone="secondary">
                    {t('Relevant applications')}
                  </Text>
                </TextLinkButton>
              </Inline>
            </Box>
          ) : (
            <TextLinkButton
              onClick={() => {
                openRelevantApplicationDialog();
              }}
            >
              <Text tone="secondary" size="small">
                {t('Relevant applications')}
              </Text>
            </TextLinkButton>
          )}
        </Inline>
      )}
    </Layout>
  );
};

const Layout: FC<{
  children: Array<JSX.Element | undefined | null | false>;
}> = ({ children }) => (
  <Box data-testid="job-stats-summary">
    <Inline space="small" alignY="center">
      {children}
    </Inline>
  </Box>
);

export const JobStatDetailSkeleton: FC = () => (
  <Layout>
    <TextSkeleton height="small" width="100px" />
    <TextSkeleton height="small" width="150px" />
  </Layout>
);

export default observer(JobStatDetail);
